import React, { useState, useRef, useEffect } from 'react';
import { Box, Chip, IconButton, useTheme } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosNewIcon from '@mui/icons-material/ArrowForwardIos';

interface ScrollableChipsProps {
  readonly options: string[];
  readonly selectedOptions: string[];
  readonly singleSelect?: boolean;
  readonly setSelectedOptions: (options: string[]) => void;
}

const ScrollableChips: React.FC<ScrollableChipsProps> = ({
  options,
  selectedOptions,
  singleSelect,
  setSelectedOptions,
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScrollButtons = () => {
    const container = containerRef.current;
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setCanScrollLeft(scrollLeft > 0);
      // Due to virtual screen scaling scrollleft can sometimes have decimal places which we don't need for our case so we truncate them. 
      // Depending on how the viewport is sized it is possible to for scollleft to be off by one pixel compared to the diff in widths so 
      // this checks one above and below to handle those sizing edge cases.
      let scrollRight = (
        Math.trunc(scrollLeft) !== scrollWidth - clientWidth
        && Math.trunc(scrollLeft) + 1 !== scrollWidth - clientWidth
        && Math.trunc(scrollLeft) - 1 !== scrollWidth - clientWidth
      );
      setCanScrollRight(scrollRight);
    }
  };

  const handleScrollLeft = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: -350, behavior: 'auto' });
    }
    checkScrollButtons();
  };

  const handleScrollRight = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: 350, behavior: 'auto' });
    }
    checkScrollButtons();
  };

  const handleChipClick = (option: string) => {
    const isOptionSelected = selectedOptions.includes(option);
    if (isOptionSelected) {
      setSelectedOptions(selectedOptions.filter((filter) => filter !== option))
    } else {
      if (singleSelect) {
        setSelectedOptions([option]);
      } else {
        setSelectedOptions([...selectedOptions, option])
      }
    }
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver(checkScrollButtons);

    resizeObserver.observe(containerRef.current!);
    window.addEventListener('resize', checkScrollButtons);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkScrollButtons);
    };
  }, []);

  useEffect(() => {
    checkScrollButtons();
  }, [options]);

  return (
    <React.Fragment>
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', overflow: 'hidden' }}>
        {canScrollLeft && (
          <IconButton onClick={handleScrollLeft}>
            <ArrowBackIosNewIcon sx={{ color: 'white' }} />
          </IconButton>
        )}
        <Box ref={containerRef} sx={{
          display: 'flex',
          overflowX: 'auto',
          overflow: 'scroll',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          flexGrow: 1,
          '& .MuiButtonBase-root': {
            border: "1px solid",
            borderRadius: "20px",
            borderColor: 'rgba(255, 255, 255, 0.3)',
            margin: "0px 7px",
            fontWeight: 400,
            fontFamily: "Inter",
            fontSize: "13px",
            color: `rgba(255, 255, 255, 1) !important`,
          },
          "& .Mui-selected": {
            color: `rgba(255, 255, 255, 1) !important`,
          },
        }}>
          {options.map((label, index) => (
            <Chip
              key={label}
              label={label}
              onClick={() => handleChipClick(label)}
              clickable={true}
              sx={{
                m: 0.5,
                margin: '0px 7px',
                backgroundColor: selectedOptions?.includes(label) ? `${theme.palette.primary.main} !important` : "",
              }}
            />
          ))}
        </Box>
        {canScrollRight && (
          <IconButton onClick={handleScrollRight}>
            <ArrowForwardIosNewIcon sx={{ color: 'white' }} />
          </IconButton>
        )}
      </Box>
    </React.Fragment>
  );
};

export default ScrollableChips;
